import { Injectable } from '@angular/core';
import { EventSearchParams } from './event-search-params.model';
import { EventEdit } from './event-edit.model';
import { EventRead } from './event-read.model';
import { EventCopy } from './event-copy.model';
import { ApiService, PagedResult } from '../../shared/index';
import { UserService } from '../../users/shared/user.service';
import { UtilService } from '../../shared/util/util.service';
import { ConfigService } from '../../config/shared/config.service';
import { AppContext } from '../../config/shared/app-context.model';
import { EventNewParams } from './new/event-new-params.model';

import { MomentService } from '../../shared/moment/moment.service';
import { NavigationService } from '../../shared/navigation/navigation.service';
import { EventsServiceApiService } from 'src/app/shared/api/events-service-api.service';
import { NotFoundError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class EventsV4Service {
  private _eventServiceBaseUrl: string = '/event/v2/event/';

  public constructor(
    private _apiService: ApiService,
    private _userService: UserService,
    private _utilService: UtilService,
    private _configService: ConfigService,
    private _momentService: MomentService,
    private _navigationService: NavigationService,
    private _eventApiService: EventsServiceApiService
  ) {}

  public getUpcomingEvents = (): Promise<any> => {
    let url: string = this._eventServiceBaseUrl + 'search';
    url +=
      '?UpcomingEventsOnly=true&SearchDateBegin=' + encodeURIComponent('1/1/1900') + '&SearchDateEnd=' + encodeURIComponent('12/31/2999');

    return this._eventApiService.get(url);
  };

  public getNextFiveOrgEvents(): Promise<PagedResult> {
    let startDate = new Date();
    let endDate = new Date();
    endDate.setMonth(endDate.getMonth() + 6);

    let url: string = this._eventServiceBaseUrl + 'search';
    url +=
      '?UpcomingEventsOnly=true&SearchDateBegin=' +
      encodeURIComponent(startDate.toDateString()) +
      '&SearchDateEnd=' +
      encodeURIComponent(endDate.toDateString()) +
      '&PageSize=5&OrganizationEvent=True';

    return this._eventApiService.get(url);
  }

  public getUpcomingOrgEventsForSixMonths(): Promise<PagedResult> {
    let startDate = new Date();
    let endDate = new Date();
    endDate.setMonth(endDate.getMonth() + 6);

    let url: string = this._eventServiceBaseUrl + 'search';
    url +=
      '?UpcomingEventsOnly=true&SearchDateBegin=' +
      encodeURIComponent(startDate.toDateString()) +
      '&SearchDateEnd=' +
      encodeURIComponent(endDate.toDateString()) +
      '&PageSize=99999&OrganizationEvent=True';

    return this._eventApiService.get(url);
  }

  public getUpcomingOrgEventsForSixMonthsPaged(pageSize: number, pageNum: number): Promise<PagedResult> {
    let startDate = new Date();
    let endDate = new Date();
    endDate.setMonth(endDate.getMonth() + 6);

    let url: string = this._eventServiceBaseUrl + 'search';
    url +=
      '?UpcomingEventsOnly=true&SearchDateBegin=' +
      encodeURIComponent(startDate.toDateString()) +
      '&SearchDateEnd=' +
      encodeURIComponent(endDate.toDateString()) +
      `&PageSize=${pageSize}&PageNumber=${pageNum}&OrganizationEvent=True`;

    return this._eventApiService.get(url);
  }

  public searchEvents = (pagedSearch: EventSearchParams): Promise<any> => {
    let eventId = pagedSearch.EventId;
    if (!eventId && /^\d+$/.test(pagedSearch.Keyword)) {
      eventId = parseInt(pagedSearch.Keyword);
    }
    let params: any = {
      PageNumber: pagedSearch.PageNumber,
      PageSize: pagedSearch.PageSize,
      OrderBy: pagedSearch.OrderBy,
      Keyword: pagedSearch.Keyword,
      EventId: pagedSearch.EventId,
      OrganizationEvent: pagedSearch.OrganizationEvent,
      CommunityEvent: pagedSearch.CommunityEvent,
      MembersOnlyEvent: pagedSearch.MembersOnlyEvent,
      InternalEvent: pagedSearch.InternalEvent,
      EventsWithPendingRegistrationsOnly: pagedSearch.EventsWithPendingRegistrationsOnly,
      SearchDateBegin: pagedSearch.SearchDateBegin,
      SearchDateEnd: pagedSearch.SearchDateEnd,
      EventType: pagedSearch.EventType?.length > 0 ? pagedSearch.EventType : undefined,
    };

    let url: string = this._eventServiceBaseUrl + 'search';
    return this._eventApiService.postPaged(url, params);
  };

  public quickSearchEvents = (pagedSearch: EventSearchParams): Promise<any> => {
    let params: any = {
      PageNumber: pagedSearch.PageNumber,
      PageSize: pagedSearch.PageSize,
      OrderBy: pagedSearch.OrderBy,
      Keyword: pagedSearch.Keyword,
      SearchDateBegin: pagedSearch.SearchDateBegin,
      SearchDateEnd: pagedSearch.SearchDateEnd,
      EventId: pagedSearch.EventId,
    };

    // set date defaults
    if (!params.SearchDateBegin) {
      params.SearchDateBegin = this._momentService.getMomentByDate(new Date()).format('YYYY-MM-DD');
    }
    if (!params.SearchDateEnd) {
      // give a 12 month date range
      params.SearchDateEnd = this._momentService.getMomentByDate(new Date(params.SearchDateBegin)).add(12, 'months').format('YYYY-MM-DD');
    }

    let url: string = this._eventServiceBaseUrl + 'search';
    return this._eventApiService.getPaged(url, params);
  };

  /**
   * export registrations. gets back url to export
   * @param pagedSearch
   */
  public getEventsExport(pagedSearch: EventSearchParams): Promise<string> {
    let eventId = pagedSearch.EventId;
    if (!eventId && /^\d+$/.test(pagedSearch.Keyword)) {
      eventId = parseInt(pagedSearch.Keyword);
    }
    let params: any = {
      PageNumber: pagedSearch.PageNumber,
      PageSize: pagedSearch.PageSize,
      OrderBy: pagedSearch.OrderBy,
      Keyword: pagedSearch.Keyword,
      EventId: pagedSearch.EventId,
      OrganizationEvent: pagedSearch.OrganizationEvent,
      CommunityEvent: pagedSearch.CommunityEvent,
      MembersOnlyEvent: pagedSearch.MembersOnlyEvent,
      InternalEvent: pagedSearch.InternalEvent,
      EventsWithPendingRegistrationsOnly: pagedSearch.EventsWithPendingRegistrationsOnly,
      SearchDateBegin: [
        pagedSearch.SearchDateBegin.getMonth() + 1,
        pagedSearch.SearchDateBegin.getDate(),
        pagedSearch.SearchDateBegin.getFullYear(),
      ].join('/'),
      SearchDateEnd: [
        pagedSearch.SearchDateEnd.getMonth() + 1,
        pagedSearch.SearchDateEnd.getDate(),
        pagedSearch.SearchDateEnd.getFullYear(),
      ].join('/'),
      EventType: pagedSearch.EventType,
    };

    // convert to string query
    let queryParams: string = Object.keys(params)
      .filter((k) => params[k] != null && !(Array.isArray(params[k]) && params[k].length === 0))
      .map((k: string) => k + '=' + encodeURIComponent(params[k]))
      .join('&');

    let url: string = this._eventServiceBaseUrl + 'csv?' + queryParams;
    return this._eventApiService.get(url);
  }

  public getUpcomingEventsPaged = (pagedSearch: EventSearchParams): Promise<any> => {
    let params: any = {
      PageNumber: pagedSearch.PageNumber,
      PageSize: pagedSearch.PageSize,
      OrderBy: pagedSearch.OrderBy,
      UpcomingEventsOnly: true,
      Keyword: pagedSearch.Keyword,
      SearchDateBegin: pagedSearch.SearchDateBegin,
      SearchDateEnd: pagedSearch.SearchDateEnd,
    };

    // set date defaults
    if (!params.SearchDateBegin) {
      params.SearchDateBegin = this._momentService.getMomentByDate(new Date()).format('YYYY-MM-DD');
    }
    if (!params.SearchDateEnd) {
      // give a 12 month date range
      params.SearchDateEnd = this._momentService.getMomentByDate(new Date(params.SearchDateBegin)).add(12, 'months').format('YYYY-MM-DD');
    }

    let url: string = this._eventServiceBaseUrl + 'search';
    return this._eventApiService.getPaged(url, params);
  };

  public getRecentlyViewed = (pagedSearch: EventSearchParams): Promise<any> => {
    let params: any = {
      PageNumber: pagedSearch.PageNumber,
      PageSize: pagedSearch.PageSize,
      OrderBy: pagedSearch.OrderBy,
      UpcomingEventsOnly: true,
      Keyword: pagedSearch.Keyword,
      SearchDateBegin: '2010-1-1',
      SearchDateEnd: '2050-1-1',
    };
    let url: string = '/Events/RecentlyViewed';
    return this._apiService.getPaged(url, params);
  };

  public getEventEdit = (eventId: number): Promise<EventEdit> => {
    let url: string = this._eventServiceBaseUrl + eventId.toString();
    return this._eventApiService.get(url).then((event: EventEdit) => {
      // fix urls in event if there's an event
      if (event) {
        var fixPromise: Array<Promise<string>> = [];
        fixPromise.push(this.setUrlsToFullPath(event.Descr).then((fixedDescr) => (event.Descr = fixedDescr)));
        fixPromise.push(
          this.setUrlsToFullPath(event.ConfirmationMessage).then(
            (fixedConfirmationMessage) => (event.ConfirmationMessage = fixedConfirmationMessage)
          )
        );

        // make sure the public registration end date has a value
        if (!event.PublicRegistrationEndDateUtc || this._momentService.getMomentByDate(event.PublicRegistrationEndDateUtc).year() <= 1900) {
          event.UsePublicRegistrationEndDate = false;
          event.PublicRegistrationEndDateUtc = null;
        } else {
          event['UsePublicRegistrationEndDate'] = true;
        }

        return Promise.all(fixPromise).then(() => event);
      } else {
        return event;
      }
    });
  };

  private getEventReadApiGetUrl = (eventId: number): string => {
    return this._eventServiceBaseUrl + eventId.toString();
  };

  public getEventRead = (eventId: number): Promise<EventRead> => {
    let url: string = this.getEventReadApiGetUrl(eventId);
    return this._eventApiService.get(url);
  };

  public createEvent = (eventNewParams: EventNewParams): Promise<any> => {
    eventNewParams.SourceUtcOffSetMinutes = this._utilService.getCurrentUtcOffSetMinutes();
    // these aren't breaking changes, so we don't care if it's version 3 (for now)
    eventNewParams.Version = 3;
    return this._eventApiService.post(this._eventServiceBaseUrl, eventNewParams);
  };

  public delete = (eventId: number): Promise<void> => {
    return this._eventApiService.delete(`${this._eventServiceBaseUrl}${eventId}`);
  };

  public deleteEventReadCache = (eventId: number): void => {
    let url: string = this.getEventReadApiGetUrl(eventId);
    this._eventApiService.deleteCache(url);
  };

  public copyEvent = (eventId: number, data: EventCopy): Promise<number> => {
    let url: string = this._eventServiceBaseUrl + 'copy';
    return this._eventApiService.post(url, data);
  };

  public updateEvent = (eventId: number, eventEdit: EventEdit): Promise<void> => {
    eventEdit.EventId = eventId;
    eventEdit.SourceUtcOffSetMinutes = this._utilService.getCurrentUtcOffSetMinutes();
    eventEdit.Version = 3;

    // this gets set to current date by default. make it "nothing" to match what the user expects.
    if (!eventEdit.UsePublicRegistrationEndDate) {
      eventEdit.PublicRegistrationEndDateUtc = new Date('1/1/1900');
    }

    return this._eventApiService.put(this._eventServiceBaseUrl, eventEdit);
  };

  /**
   * returns SignUpsAndAttendance report url to the legacy webclient with the appropriate web client secure domain and legacy token
   * @param eventId
   */
  public getSignUpsAndAttendenceReportUrl(eventId: number): Promise<string> {
    return this._userService.getCurrentUserLegacyToken().then((token: string) => {
      let url =
        this._navigationService.getLegacyDomain() +
        '/external/wcpages/v12/webclient/report/report.aspx?reportname=private_Events_EventsSignUpsAndAttendence&EventId=' +
        eventId.toString() +
        '&token=' +
        token;

      return url;
    });
  }

  /**
   * returns name badge report url to the legacy webclient with the appropriate web client secure domain and legacy token
   * @param eventId
   */
  public getNameBadgeReportUrl(eventId: number): Promise<string> {
    return this._userService.getCurrentUserLegacyToken().then((token: string) => {
      let url =
        this._navigationService.getLegacyDomain() +
        '/external/wcpages/v12/webclient/report/report.aspx?reportname=private_Events_NameBadge_Avery5383&recordselection=%7bEventList.EventID%7d+in+%5b' +
        eventId.toString() +
        '%5d' +
        '&token=' +
        token;

      return url;
    });
  }

  /**
   * helper to change image paths to urls
   * @param value
   */
  public setUrlsToFullPath(value: string): Promise<string> {
    if (value == null) return Promise.resolve(value);

    return this._configService.getAppContext().then((data: AppContext) => {
      value = this._utilService.replaceAll(value, "src='/", "src='" + data.WebRootSecure + '/');
      value = this._utilService.replaceAll(value, 'src="/', 'src="' + data.WebRootSecure + '/');
      return value;
    });
  }
}
